import { DeploymentStatus } from '../../constant/Constants';
import { VariableProperties } from '../datatype/VariablePropertiesDomains';
import { MLOpsDatasourceSchema, MLOpsMetric } from './MLOpsSchemaDomains';
import { AccessBase } from '../access/BaseAccess';
import { CustomFilter } from '../ui/FilterDomains';
import { Syntax } from '@trlogic/ui-pack';

export interface MLOpsModel extends AccessBase {
  name: string;
  description: string;
  channel: string;
  version: string;
  trainingDatasource: MLOpsDatasourceSchema;
  validationDatasource: MLOpsDatasourceSchema;
  baseFeatures: MLOpsModelBaseFeature[];
  customFeatures: MLOpsModelCustomFeature[];
  samplingRatio: number | undefined;
  algorithms: MLOpsModelAlgorithm[];
  parameters: MLOpsModelParameters;
  stage: DeploymentStatus;
}

export interface MLOpsDatasourceFile extends MLOpsDatasourceSchema {
  path: string;
  separator: string;
  fileName: string;
  size: number;
}

export interface MLOpsDatasourceHistorical extends MLOpsDatasourceSchema {
  query: CustomFilter;
  riskType: 'FRAUD' | 'COMPLIANCE';
}

export interface MLOpsDatasourceDatabase extends MLOpsDatasourceSchema {
  connectionName: string;
  query: string;
  timeout: number;
}

export interface MLOpsModelFeature {
  name: string;
  description?: string;
  properties: VariableProperties;
}

export interface MLOpsModelBaseFeature extends MLOpsModelFeature {
  enabled: boolean;
}

export interface MLOpsModelCustomFeature extends MLOpsModelFeature {
  expression: Syntax;
}

export type MLOpsModelAlgorithm = 'glm' | 'xgboost' | 'gbm' | 'drf' | 'stackedEnsemble' | 'deepLearning';

export interface MLOpsModelParameters {
  maxModels?: number;
  maxRuntimeInMinutes?: number;
  sortMetric: MLOpsMetric;
  stoppingMetric: MLOpsMetric;
  seed?: number;
}

export interface MLOpsModelResultFeature {
  feature: string;
  percentage: number;
  scaledImportance: number;
  relativeImportance: number;
}

export interface MLOpsModelResult {
  id: string;
  name: string;
  version: string;
  model: string;
  leader: boolean;
  mse: number;
  rmse: number;
  nobs: number;
  r2: number;
  mrd: number;
  mae: number;
  rmsle: number;
  featureImportances: MLOpsModelResultFeature[];
}

export enum MLOpsMetricResultType {
  ROC_CURVE = 'roc_curve',
  CONFUSION_MATRIX = 'confusion_matrix',
  FPR_FNR_THRESHOLD = 'fpr_fnr_threshold',
  TPR_TNR_THRESHOLD = 'tpr_tnr_threshold',
  PRECISION_RECALL = 'precision_recall',
  MCC_THRESHOLD = 'mcc_threshold',
  GMEAN_THRESHOLD = 'gmean_threshold',
  FBETA_THRESHOLD = 'fbeta_threshold',
  BALANCED_ACCURACY_THRESHOLD = 'balanced_accuracy_threshold',
  F1_THRESHOLD = 'f1_threshold',
}

export interface MLOpsMetricResult {
  id: string;
  name: string;
  version: string;
  threshold: number;
  tpc: number;
  fpc: number;
  fnc: number;
  tnc: number;
  tpr: number;
  fpr: number;
  fnr: number;
  tnr: number;
  accuracy: number;
  precision: number;
  f1score: number;
  npv: number;
  fdr: number;
  balancedAccuracy: number;
  mcc: number;
  fBetaScore: number;
  gMean: number;
}
